// Port of ios/Sources/ViewModels/AttachmentKind.swift.
//
// Pure presentation decisions for attachments — what KIND of card a file gets, the voice
// player's clocks and rate cycle. Mirrors Android's `VoiceNoteFiles` + card classification so
// the two clients render the same file the same way, and every rule here is unit-tested.
import { language } from '../i18n/i18n';

export type AttachmentKindValue = 'image' | 'voice' | 'file';

const audioExtensions = new Set(['m4a', 'mp3', 'ogg', 'opus', 'wav', 'aac', 'weba', 'flac']);

/** `NSString.pathExtension`: the text after the last "." of the last path component, or "". */
function pathExtension(name: string): string {
  const component = name.slice(name.lastIndexOf('/') + 1);
  const dot = component.lastIndexOf('.');
  return dot < 0 ? '' : component.slice(dot + 1);
}

/**
 * Any audio file is the voice player, whatever produced it (this client's recorder,
 * Android's, the desktop's) — keying on content, not post type, is what makes the platforms
 * interoperate without knowing about each other. Images by mime OR real dimensions, because
 * relayed files often arrive as application/octet-stream.
 */
function classify(mimeType: string, name: string, width = 0, height = 0): AttachmentKindValue {
  if (mimeType.startsWith('audio/')) return 'voice';
  const ext = pathExtension(name).toLowerCase();
  if (audioExtensions.has(ext)) return 'voice';
  if (mimeType.startsWith('image/')) return 'image';
  if (width > 0 && height > 0) return 'image';
  return 'file';
}

function pad2(n: number): string {
  return n < 10 ? `0${n}` : `${n}`;
}

/** 0:07, 1:23, 1:02:03 — a clock, never raw seconds. */
function clock(seconds: number): string {
  const total = Math.max(0, Math.floor(Number.isFinite(seconds) ? seconds : 0));
  if (total >= 3600) {
    return `${Math.floor(total / 3600)}:${pad2(Math.floor((total % 3600) / 60))}:${pad2(total % 60)}`;
  }
  return `${Math.floor(total / 60)}:${pad2(total % 60)}`;
}

/** The classic cycle: 1 → 1.25 → 1.5 → 2 → home. */
function nextRate(current: number): number {
  if (current < 1.25) return 1.25;
  if (current < 1.5) return 1.5;
  if (current < 2) return 2;
  return 1;
}

/**
 * "2.4 MB" for the file card, the way Finder would say it.
 *
 * Swift uses `ByteCountFormatter(countStyle: .file)`, which has no Intl equivalent; this
 * reproduces its observable rules: decimal units (1 KB = 1000 bytes), whole KB, one fraction
 * digit for MB, two for GB and above, trailing zeros dropped, "Zero KB" for nothing and
 * "N bytes" under a kilobyte. Not covered by the Swift tests; the numbers are what matter.
 */
function sizeLabel(bytes: number, locale?: string): string {
  const spanish = locale ? locale.toLowerCase().startsWith('es') : language() === 'es';
  const tag = locale ?? (spanish ? 'es-MX' : 'en-US');
  if (bytes === 0) return spanish ? 'Cero KB' : 'Zero KB';
  const abs = Math.abs(bytes);
  if (abs < 1000) return bytes === 1 || bytes === -1 ? `${bytes} byte` : `${bytes} bytes`;
  const units = ['KB', 'MB', 'GB', 'TB', 'PB'];
  let value = bytes / 1000;
  let unit = 0;
  while (Math.abs(value) >= 1000 && unit < units.length - 1) {
    value /= 1000;
    unit += 1;
  }
  const digits = unit === 0 ? 0 : unit === 1 ? 1 : 2;
  const number = new Intl.NumberFormat(tag, { maximumFractionDigits: digits, useGrouping: true }).format(value);
  return `${number} ${units[unit]}`;
}

export const AttachmentKind = {
  audioExtensions,
  classify,
  clock,
  nextRate,
  sizeLabel,
} as const;
